import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { METER_CONTENT } from '../content/meter-content';
import {
	useOrgUserCountErrorMessage,
	useSelector,
	useSetMeter,
	useSetOrgUserCount,
} from '../context/hooks';
import { selectCanAdvanceUsageStep, selectMeter, selectOrgUserCount } from '../context/selectors';
import { useUbpStep1ScreenViewedEvent } from '../ubp-calculator-analytics';

import { ChoiceCard } from './choice-card';
import { NumericInput } from './inputs/numeric-input';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';
import { METER_LABEL_MESSAGES } from './strings';

import type { BulletOrIconListItem } from './strings';
import type { LogoBrandV3 } from '../../../../libraries/wpl/foundations/src/logo-v3';
import type { Meter } from '../context/types';

type MeterEntry = {
	label: string;
	description: string | ReactNode;
	icon?: LogoBrandV3;
	list?: BulletOrIconListItem[];
	info?: string | ReactNode;
};

// Source identifier attached to every analytics event fired from this step.
// Used by downstream analytics consumers to filter/segment Usage-step events.
const ANALYTICS_SOURCE = 'ubpCalculatorUsageStep';

export const UsageStep = () => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);
	const setMeter = useSetMeter();
	const orgUserCount = useSelector(selectOrgUserCount);
	const setOrgUserCount = useSetOrgUserCount();
	const orgUserCountError = useOrgUserCountErrorMessage();
	const canAdvance = useSelector(selectCanAdvanceUsageStep);
	const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireStep1Viewed = useUbpStep1ScreenViewedEvent();

	useEffect(() => {
		fireStep1Viewed();
		// Intentionally run once on mount only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fires only when the selection actually CHANGES — re-clicking the
	// same card is a no-op (both setMeter and analytics are skipped).
	const handleMeterSelect = (choice: Meter) => {
		if (choice === meter) return;
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'meterChoiceCard',
			actionSubjectId: choice,
			attributes: { previousMeter: meter, selectedMeter: choice },
			source: ANALYTICS_SOURCE,
		}).fire();
		setMeter(choice);
	};

	// Fires a `numberInput committed` analytics event when the user
	// finishes editing the org-size input (onBlur is the semantic commit
	// point for a free-form number field).
	const handleOrgUserCountBlur = (value: number | undefined) => {
		createAnalyticsEvent({
			eventType: 'track',
			action: 'committed',
			actionSubject: 'numberInput',
			actionSubjectId: 'orgUserCount',
			attributes: {
				value: value ?? null,
				hasError: Boolean(orgUserCountError),
			},
			source: ANALYTICS_SOURCE,
		}).fire();
	};

	// Fires immediately before StepNav advances to the next step.
	// Sets hasAttemptedNext so validation errors become visible, and
	// returns false to abort navigation if the step isn't complete.
	const handleBeforeNext = () => {
		setHasAttemptedNext(true);
		// Force-sync the context error state so the error message renders
		// even on first visit (when the user hasn't typed anything yet and
		// orgUserCountError is still undefined in context).
		setOrgUserCount(orgUserCount);
		if (!canAdvance) return false;
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'usageStepNext',
			attributes: { meter, orgUserCount },
			source: ANALYTICS_SOURCE,
		}).fire();
	};

	// Consumes the atomic per-meter content registry so content authors
	// can update copy for one meter without touching this file. See
	// `src/content/meter-content.ts` for the registry.
	const USAGE_STRINGS = useMemo<Record<Meter, MeterEntry>>(
		() =>
			(Object.keys(METER_CONTENT) as Meter[]).reduce(
				(acc, key) => {
					acc[key] = {
						label: intl.formatMessage(METER_LABEL_MESSAGES[key]),
						description: intl.formatMessage(METER_CONTENT[key].description),
					};
					return acc;
				},
				{} as Record<Meter, MeterEntry>,
			),
		[intl],
	);

	const usageMeters = Object.keys(USAGE_STRINGS) as Meter[];

	return (
		<TypographyStyles>
			<Stepper visibleStepCount={3} />
			<h1 css={pageTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.usage.pageTitle',
					defaultMessage: 'Let\u2019s figure out your usage needs',
				})}
			</h1>
			<h3 id="usage-step-heading" css={sectionTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.usage.interestQuestion',
					defaultMessage: 'What are you interested in?',
				})}
			</h3>
			<div css={[cardGridStyle]} role="radiogroup" aria-labelledby="usage-step-heading">
				{usageMeters.map((choice: Meter) => {
					const { label, description, list, info } = USAGE_STRINGS[choice];
					return (
						<ChoiceCard
							key={`usage-${choice}`}
							value={choice}
							selected={meter === choice}
							onSelect={() => handleMeterSelect(choice)}
							groupName="group-usage"
							label={label}
							description={description}
							list={list}
							info={info}
						/>
					);
				})}
			</div>
			<div css={orgSizeQuestionStyle}>
				<h3 css={orgSizeHeadingStyle} id="orgUserCount-label">
					<label htmlFor="orgUserCount">
						{intl.formatMessage({
							id: 'ubp.calculator.usage.orgSizeQuestion',
							defaultMessage: 'How many people are in your organization?',
						})}
					</label>
				</h3>
				<div css={inputAndRequiredStyle}>
					<div css={inputWithIconStyle}>
						<NumericInput
							id="orgUserCount"
							value={orgUserCount}
							min={0}
							invalid={Boolean(hasAttemptedNext && orgUserCountError)}
							aria-required={true}
							aria-labelledby="orgUserCount-label"
							onChange={setOrgUserCount}
							onBlur={handleOrgUserCountBlur}
						/>
						{hasAttemptedNext && orgUserCountError && (
							<svg
								css={errorIcon}
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM9.29289 11.7071C9.48043 11.8946 9.73478 12 10 12C10.2652 12 10.5196 11.8946 10.7071 11.7071C10.8946 11.5196 11 11.2652 11 11V6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6V11C9 11.2652 9.10536 11.5196 9.29289 11.7071ZM9.29289 14.7071C9.48043 14.8946 9.73478 15 10 15C10.2652 15 10.5196 14.8946 10.7071 14.7071C10.8946 14.5196 11 14.2652 11 14C11 13.7348 10.8946 13.4804 10.7071 13.2929C10.5196 13.1054 10.2652 13 10 13C9.73478 13 9.48043 13.1054 9.29289 13.2929C9.10536 13.4804 9 13.7348 9 14C9 14.2652 9.10536 14.5196 9.29289 14.7071Z"
									fill="#AE2A19"
								/>
							</svg>
						)}
						<div css={errorMessageStyle}>{(hasAttemptedNext && orgUserCountError) || ' '}</div>
					</div>
					<span css={requiredLabelStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.usage.required',
							defaultMessage: 'Required field',
						})}
					</span>
				</div>
			</div>
			<StepNav showNext onBeforeNext={handleBeforeNext} />
		</TypographyStyles>
	);
};

// H1 title styling — extra size overrides for the Figma "D - H1 - Small" (48/56).
// TypographyStyles provides the base font-family/weight; centering + explicit
// 40px vertical padding come from Figma spec (40 above and below the H1).
const pageTitleStyle = css({
	margin: 0,
	textAlign: 'center',
	paddingBlock: '40px',
});

// First H3 — sits above the meter/choice cards. 40px padding below per Figma;
// the H1 above supplies its own 40px bottom padding so no top padding is needed.
const sectionTitleStyle = css({
	margin: 0,
	paddingBlockEnd: '40px',
});

// Second H3 — the "How many people..." question that sits on the SAME row as
// the org-size input. The 60px top padding lives on the parent row wrapper
// (orgSizeQuestionStyle) so the H3 and input stay vertically centered on the
// same baseline; no vertical padding is needed here.
const orgSizeHeadingStyle = css({
	margin: 0,
});

const cardGridStyle = css({
	display: 'grid',
	gap: token('space.400'),
	gridTemplateColumns: '1fr 1fr',
	// Mobile (< 768px) stacks the meter choice cards into a single column
	// per Figma mobile spec. See needs-step for the parallel treatment.
	[media.below.sm]: {
		gap: token('space.300'),
		gridTemplateColumns: '1fr',
	},
});

const orgSizeQuestionStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: token('space.200'),
	flexWrap: 'wrap',
	paddingBlockStart: '60px',
	// Mobile stacks the H3 above the input+Required cluster so the input
	// can breathe and reach a comfortable tap-target width.
	[media.below.md]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
});

const inputAndRequiredStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	flexWrap: 'wrap',
	textAlign: 'left',
	gap: token('space.100'),
});

const inputWithIconStyle = css({
	width: '160px',
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	[media.below.md]: {
		width: '200px',
	},
});

const errorIcon = css({
	height: '20px',
	width: '20px',
	position: 'absolute',
	insetInlineEnd: '12px',
	insetBlockStart: '10px',
	pointerEvents: 'none',
});

const requiredLabelStyle = css({
	fontSize: '0.875rem',
	lineHeight: '40px',
	color: wacToken('color.neutral.light1100'),
});

const errorMessageStyle = css({
	flexBasis: '100%',
	marginBlockStart: '4px',
	fontSize: '0.75rem',
	color: wacToken('color.secondary.red'),
	textAlign: 'left',
	minHeight: '3rem',
});
